/**
 * Device Updater Logger Service
 * Provides logging with download and backend transmission capabilities
 */

import { ILogger, LogEntry, LogLevel } from '../types/imx-types';

/**
 * Logger Service Implementation
 */
export class UpdaterLoggerService implements ILogger {
  private entries: LogEntry[] = [];
  private maxEntries: number;
  private sessionId: string;
  private eid: string | null = null;

  constructor(
    maxEntries = 10000,
    private backendUrl?: string
  ) {
    this.maxEntries = maxEntries;
    this.sessionId = this.generateSessionId();
  }

  /**
   * Log a message
   */
  log(message: string, level: LogLevel = LogLevel.INFO, data?: unknown): void {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      data,
    };

    this.entries.push(entry);

    // Limit entries to prevent memory issues
    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, data?: unknown): void {
    this.log(message, LogLevel.DEBUG, data);
  }

  /**
   * Log info message
   */
  info(message: string, data?: unknown): void {
    this.log(message, LogLevel.INFO, data);
  }

  /**
   * Log success message
   */
  success(message: string, data?: unknown): void {
    this.log(message, LogLevel.SUCCESS, data);
  }

  /**
   * Log warning message
   */
  warning(message: string, data?: unknown): void {
    this.log(message, LogLevel.WARNING, data);
  }

  /**
   * Log error message
   */
  error(message: string, data?: unknown): void {
    this.log(message, LogLevel.ERROR, data);
  }

  /**
   * Get all log entries
   */
  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  /**
   * Get entries for a specific level
   */
  getEntriesByLevel(level: LogLevel): LogEntry[] {
    return this.entries.filter((entry) => entry.level === level);
  }

  /**
   * Get entries in time range
   */
  getEntriesInRange(start: Date, end: Date): LogEntry[] {
    return this.entries.filter(
      (entry) => entry.timestamp >= start && entry.timestamp <= end
    );
  }

  /**
   * Clear all log entries
   */
  clear(): void {
    this.entries = [];
    this.info('Log cleared');
  }

  /**
   * Set EID for session context
   */
  setEID(eid: string): void {
    this.eid = eid;
    this.info(`Session EID set: ${eid}`);
  }

  /**
   * Download logs as text file
   */
  download(filename?: string): void {
    const logText = this.formatLogsAsText();
    const blob = new Blob([logText], { type: 'text/plain;charset=utf-8' });

    const defaultFilename = `updater-log-${this.sessionId}-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    const finalFilename = filename || defaultFilename;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    this.info(`Logs downloaded: ${finalFilename}`);
  }

  /**
   * Download logs with EID in filename for easy identification
   */
  downloadLogs(eid?: string): void {
    const eidPart = eid || this.eid || 'unknown';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `plates-update-${eidPart}-${timestamp}.txt`;
    this.download(filename);
  }

  /**
   * Upload logs to backend
   */
  async uploadToBackend(sessionId?: string): Promise<void> {
    if (!this.backendUrl) {
      throw new Error('Backend URL not configured');
    }

    const payload = {
      sessionId: sessionId || this.sessionId,
      eid: this.eid,
      timestamp: new Date().toISOString(),
      entries: this.entries,
      metadata: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
      },
    };

    try {
      const response = await fetch(this.backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Backend returned ${response.status}: ${response.statusText}`);
      }

      this.success('Logs uploaded to backend successfully');
    } catch (error) {
      this.error(`Failed to upload logs to backend: ${(error as Error).message}`);
      throw error;
    }
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get summary statistics
   */
  getSummary(): {
    total: number;
    byLevel: Record<LogLevel, number>;
    duration: number;
  } {
    const byLevel: Record<LogLevel, number> = {
      [LogLevel.DEBUG]: 0,
      [LogLevel.INFO]: 0,
      [LogLevel.SUCCESS]: 0,
      [LogLevel.WARNING]: 0,
      [LogLevel.ERROR]: 0,
    };

    for (const entry of this.entries) {
      byLevel[entry.level]++;
    }

    const duration =
      this.entries.length > 1
        ? this.entries[this.entries.length - 1].timestamp.getTime() -
          this.entries[0].timestamp.getTime()
        : 0;

    return {
      total: this.entries.length,
      byLevel,
      duration,
    };
  }

  // ========================================================================
  // Private Methods
  // ========================================================================

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Format logs as plain text
   */
  private formatLogsAsText(): string {
    const lines: string[] = [];

    // Header
    lines.push('='.repeat(80));
    lines.push('Device Updater Session Log');
    lines.push(`Session ID: ${this.sessionId}`);
    if (this.eid) {
      lines.push(`Device EID: ${this.eid}`);
    }
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push(`User Agent: ${navigator.userAgent}`);
    lines.push('='.repeat(80));
    lines.push('');

    // Summary
    const summary = this.getSummary();
    lines.push('Summary:');
    lines.push(`  Total Entries: ${summary.total}`);
    lines.push(`  Duration: ${(summary.duration / 1000).toFixed(2)}s`);
    lines.push(`  Errors: ${summary.byLevel[LogLevel.ERROR]}`);
    lines.push(`  Warnings: ${summary.byLevel[LogLevel.WARNING]}`);
    lines.push(`  Info: ${summary.byLevel[LogLevel.INFO]}`);
    lines.push(`  Success: ${summary.byLevel[LogLevel.SUCCESS]}`);
    lines.push(`  Debug: ${summary.byLevel[LogLevel.DEBUG]}`);
    lines.push('');
    lines.push('-'.repeat(80));
    lines.push('');

    // Entries
    for (const entry of this.entries) {
      const timestamp = entry.timestamp.toISOString();
      const level = entry.level.toUpperCase().padEnd(8);
      let line = `[${timestamp}] [${level}] ${entry.message}`;

      if (entry.data !== undefined) {
        const dataStr =
          typeof entry.data === 'string'
            ? entry.data
            : JSON.stringify(entry.data, null, 2);
        line += `\n    Data: ${dataStr}`;
      }

      lines.push(line);
    }

    lines.push('');
    lines.push('='.repeat(80));
    lines.push('End of Log');
    lines.push('='.repeat(80));

    return lines.join('\n');
  }
}
